<template>
  <v-card width="100%" outlined>
    <v-card-title>
      <v-icon class="mr-2">mdi-account-edit-outline</v-icon>
      แก้ไขข้อมูลการเรียน
    </v-card-title>

    <v-tabs v-model="levelTab" grow @change="changeLevel">
      <v-tabs-slider color="yellow" />
      <v-tab>ค้นหาและดูประวัติการแก้ไข</v-tab>
      <v-tab>ระดับปริญญาตรี</v-tab>
      <v-tab>ระดับบัณฑิตศึกษา</v-tab>
    </v-tabs>

    <v-card-text v-if="levelTab > 0" class="pt-6">
      <v-row align="start">
        <v-col cols="12" class="student-id-column">
          <v-text-field
            v-model.trim="studentId"
            label="รหัสนักศึกษา"
            maxlength="8"
            outlined
            dense
            clearable
            :error-messages="studentIdError"
            @input="handleStudentIdInput"
            @keyup.enter="searchStudent"
          />
        </v-col>
        <v-col cols="12" class="student-search-column">
          <v-btn
            color="success"
            block
            :loading="studentLoading"
            :disabled="!validStudentId"
            @click="searchStudent"
          >
            <v-icon left>mdi-magnify</v-icon>
            ค้นหา
          </v-btn>
        </v-col>
      </v-row>

      <v-alert v-if="searchError" type="error" text dense>
        {{ searchError }}
      </v-alert>

      <template v-if="student">
        <v-card class="mb-5" outlined>
          <v-card-title class="subtitle-1 font-weight-bold">
            {{ student.full_name }}
            <v-chip class="ml-3" color="primary" small outlined>
              {{ student.level_name }}
            </v-chip>
          </v-card-title>
          <v-card-text>
            <v-row dense>
              <v-col cols="12" sm="6"><strong>รหัสนักศึกษา:</strong> {{ student.student_id }}</v-col>
              <v-col cols="12" sm="6"><strong>สถานะการศึกษา:</strong> {{ student.status || '-' }}</v-col>
              <v-col cols="12" sm="6"><strong>คณะ:</strong> {{ student.faculty_name || '-' }}</v-col>
              <v-col cols="12" sm="6"><strong>สาขา/หลักสูตร:</strong> {{ student.program_name || '-' }}</v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <v-tabs v-model="workTab" show-arrows>
          <v-tabs-slider color="yellow" />
          <v-tab>แก้ไขสถานะนักศึกษา</v-tab>
          <v-tab>แก้ไขผลการเรียน</v-tab>
        </v-tabs>

        <v-tabs-items v-model="workTab" touchless>
          <v-tab-item>
            <v-card flat class="pt-5">
              <v-alert
                v-if="student.can_restore_status"
                type="warning"
                text
              >
                ระบบจะเปลี่ยนสถานะจาก <strong>{{ student.status }}</strong>
                เป็น <strong>{{ student.restore_status }}</strong> และลบข้อมูลลาออกกับข้อมูลประมวลผลสถานะที่เกี่ยวข้อง
              </v-alert>
              <v-alert v-else type="info" text>
                แก้ไขได้เฉพาะสถานะในกลุ่ม พ้นสภาพ / ลาออก / ตกให้ออก เท่านั้น
              </v-alert>

              <v-textarea
                v-model.trim="statusReference"
                label="เอกสารอ้างอิงหรือหมายเหตุ *"
                outlined
                counter="1000"
                maxlength="1000"
                rows="3"
                :disabled="!student.can_restore_status"
              />
              <v-btn
                color="warning"
                :disabled="!canRestoreStatus"
                @click="statusDialog = true"
              >
                <v-icon left>mdi-account-reactivate-outline</v-icon>
                คืนสถานะเป็น {{ student.restore_status }}
              </v-btn>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card flat class="pt-5">
              <v-select
                v-model="selectedTerm"
                :items="termOptions"
                label="ปีการศึกษา"
                outlined
                dense
                :disabled="termOptions.length === 0"
                @change="loadGrades"
              />

              <v-alert v-if="termOptions.length === 0" type="info" text dense>
                ไม่พบปี/ภาคการศึกษาที่นักศึกษามีข้อมูลการลงทะเบียน
              </v-alert>

              <v-data-table
                v-else
                :headers="gradeHeaders"
                :items="grades"
                :loading="gradesLoading"
                :items-per-page="10"
                no-data-text="ไม่พบรายวิชาในปี/ภาคการศึกษาที่เลือก"
              >
                <template v-slot:[gradeSlot]="{ item }">
                  <v-chip small :color="item.grade ? 'primary' : 'grey'" outlined>
                    {{ item.grade || 'ยังไม่ประกาศ' }}
                  </v-chip>
                </template>
                <template v-slot:[gradeActionSlot]="{ item }">
                  <v-btn color="warning" small @click="openGradeDialog(item)">
                    <v-icon left small>mdi-pencil</v-icon>
                    แก้ไข
                  </v-btn>
                </template>
              </v-data-table>
            </v-card>
          </v-tab-item>

        </v-tabs-items>
      </template>
    </v-card-text>

    <v-card-text v-else class="pt-6">
      <history-panel
        ref="historyPanel"
        :student-id="student ? student.student_id : ''"
        :initial-items="history"
        :loading="historyLoading"
        @search="searchHistory"
      />
    </v-card-text>

    <v-dialog v-model="gradeDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>แก้ไขผลการเรียน</v-card-title>
        <v-card-text v-if="editingGrade">
          <div class="mb-4">
            <strong>{{ editingGrade.subject_id }}</strong>
            {{ editingGrade.subject_name }}<br>
            เกรดเดิม: <strong>{{ editingGrade.grade || 'ยังไม่ประกาศ' }}</strong>
          </div>
          <v-select
            v-model="newGrade"
            :items="gradeOptions"
            label="เกรดใหม่"
            outlined
            dense
          />
          <v-textarea
            v-model.trim="gradeReference"
            label="อ้างอิงข้อมูลจากเอกสารใด *"
            hint="เช่น เลขที่หนังสือ วันที่ หรือรายละเอียดเอกสาร"
            persistent-hint
            outlined
            counter="1000"
            maxlength="1000"
            rows="3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="gradeSaving" @click="closeGradeDialog">ยกเลิก</v-btn>
          <v-btn
            color="success"
            :loading="gradeSaving"
            :disabled="!canSaveGrade"
            @click="saveGrade"
          >บันทึก</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="statusDialog" max-width="520" persistent>
      <v-card>
        <v-card-title>ยืนยันการคืนสถานะนักศึกษา</v-card-title>
        <v-card-text v-if="student">
          ต้องการเปลี่ยนสถานะของ <strong>{{ student.student_id }} {{ student.full_name }}</strong>
          จาก <strong>{{ student.status }}</strong> เป็น <strong>{{ student.restore_status }}</strong> ใช่หรือไม่
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="statusSaving" @click="statusDialog = false">ยกเลิก</v-btn>
          <v-btn
            color="warning"
            :loading="statusSaving"
            :disabled="!canRestoreStatus"
            @click="restoreStatus"
          >ยืนยัน</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import AcademicDataEditProvider from '@/services/academic_data_edit.provider'
import HistoryPanel from '@/views/registration/components/AcademicDataEditHistory.vue'

const AcademicDataEditService = new AcademicDataEditProvider()

export default {
  name: 'AcademicDataEdit',
  components: { HistoryPanel },
  data: () => ({
    gradeSlot: 'item.grade',
    gradeActionSlot: 'item.action',
    levelTab: 0,
    workTab: 0,
    studentId: '',
    student: null,
    studentLoading: false,
    studentSearchToken: 0,
    searchError: '',
    selectedTerm: null,
    grades: [],
    gradesLoading: false,
    gradeDialog: false,
    editingGrade: null,
    newGrade: null,
    gradeReference: '',
    gradeSaving: false,
    statusReference: '',
    statusDialog: false,
    statusSaving: false,
    history: [],
    historyLoading: false,
    gradeHeaders: [
      { text: 'รหัสวิชา', value: 'subject_id', width: 130 },
      { text: 'ชื่อวิชา', value: 'subject_name' },
      { text: 'กลุ่ม', value: 'section', width: 90 },
      { text: 'หน่วยกิต', value: 'credit', width: 100 },
      { text: 'เกรด', value: 'grade', width: 130 },
      { text: '', value: 'action', sortable: false, align: 'end', width: 120 },
    ],
    gradeOptions: [
      { text: 'ยังไม่ประกาศ', value: '__EMPTY__' },
      ...['A', 'B+', 'B', 'C+', 'C', 'D+', 'D', 'F', 'FA', 'FE', 'FW', 'W', 'I', 'S', 'U', 'O', 'G', 'P']
        .map((grade) => ({ text: grade, value: grade })),
    ],
  }),
  computed: {
    levelGroup() {
      return this.levelTab === 1 ? 'undergraduate' : 'graduate'
    },
    validStudentId() {
      return /^[0-9]{8}$/.test(this.studentId || '')
    },
    studentIdError() {
      return this.studentId && !this.validStudentId ? 'กรุณากรอกรหัสนักศึกษาเป็นตัวเลข 8 หลัก' : ''
    },
    termOptions() {
      if (!this.student || !Array.isArray(this.student.academic_terms)) return []
      return this.student.academic_terms.map((term) => ({
        text: `${term.year}/${term.semester}`,
        value: `${term.year}|${term.semester}`,
      }))
    },
    canSaveGrade() {
      if (!this.editingGrade || this.newGrade === null || !this.gradeReference) return false
      const normalized = this.newGrade === '__EMPTY__' ? '' : this.newGrade
      return normalized !== this.editingGrade.grade
    },
    canRestoreStatus() {
      return !!(this.student && this.student.can_restore_status && this.statusReference.trim())
    },
  },
  mounted() {
    this.searchHistory({
      action: 'all',
      student_id: '',
      editor_id: '',
      limit: 15,
    })
  },
  methods: {
    changeLevel(tab) {
      this.studentSearchToken += 1
      this.studentLoading = false
      this.studentId = ''
      this.clearStudentData()
      this.workTab = 0
      this.$nextTick(() => {
        if (this.$refs.historyPanel) this.$refs.historyPanel.clearStudentId()
      })
      if (tab === 0) return
    },
    clearStudentData() {
      this.student = null
      this.searchError = ''
      this.selectedTerm = null
      this.grades = []
      this.statusReference = ''
    },
    handleStudentIdInput(value) {
      this.studentSearchToken += 1
      this.studentLoading = false
      this.clearStudentData()
      if (/^[0-9]{8}$/.test(value || '')) {
        this.$nextTick(() => this.searchStudent())
      }
    },
    errorMessage(error, fallback) {
      const response = error && error.response && error.response.data
      if (!response) return fallback
      const message = response.message || response
      return typeof message === 'string' ? message : JSON.stringify(message)
    },
    async searchStudent() {
      if (!this.validStudentId) return
      const searchToken = ++this.studentSearchToken
      const studentId = this.studentId
      const levelGroup = this.levelGroup
      this.studentLoading = true
      this.searchError = ''
      this.student = null
      this.grades = []
      this.selectedTerm = null
      this.statusReference = ''
      try {
        const student = await AcademicDataEditService.getStudent(levelGroup, studentId)
        if (searchToken !== this.studentSearchToken) return
        this.student = student
        if (this.termOptions.length > 0) {
          this.selectedTerm = this.termOptions[0].value
          await this.loadGrades()
        }
      } catch (error) {
        if (searchToken !== this.studentSearchToken) return
        this.searchError = this.errorMessage(error, 'ไม่สามารถค้นหาข้อมูลนักศึกษาได้')
      } finally {
        if (searchToken === this.studentSearchToken) this.studentLoading = false
      }
    },
    async loadGrades() {
      if (!this.student || !this.selectedTerm) return
      const [year, semester] = this.selectedTerm.split('|')
      this.gradesLoading = true
      try {
        this.grades = await AcademicDataEditService.getGrades({
          student_type: this.student.student_type,
          student_id: this.student.student_id,
          year,
          semester,
        })
      } catch (error) {
        this.grades = []
        this.$toasted.error(this.errorMessage(error, 'ไม่สามารถโหลดผลการเรียนได้'))
      } finally {
        this.gradesLoading = false
      }
    },
    openGradeDialog(item) {
      this.editingGrade = item
      this.newGrade = item.grade || '__EMPTY__'
      this.gradeReference = ''
      this.gradeDialog = true
    },
    closeGradeDialog() {
      this.gradeDialog = false
      this.editingGrade = null
      this.newGrade = null
      this.gradeReference = ''
    },
    async saveGrade() {
      if (!this.canSaveGrade) return
      const [year, semester] = this.selectedTerm.split('|')
      this.gradeSaving = true
      try {
        await AcademicDataEditService.updateGrade({
          student_type: this.student.student_type,
          student_id: this.student.student_id,
          year,
          semester,
          subject_id: this.editingGrade.subject_id,
          section: this.editingGrade.section,
          new_grade: this.newGrade === '__EMPTY__' ? '' : this.newGrade,
          reference_document: this.gradeReference,
        })
        this.$toasted.success('แก้ไขผลการเรียนเรียบร้อยแล้ว')
        this.closeGradeDialog()
        await this.loadGrades()
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'ไม่สามารถแก้ไขผลการเรียนได้'))
      } finally {
        this.gradeSaving = false
      }
    },
    async restoreStatus() {
      this.statusSaving = true
      try {
        await AcademicDataEditService.restoreStudentStatus({
          student_type: this.student.student_type,
          student_id: this.student.student_id,
          reference_document: this.statusReference,
        })
        this.$toasted.success('คืนสถานะนักศึกษาเรียบร้อยแล้ว')
        this.statusDialog = false
        await this.searchStudent()
        this.workTab = 0
      } catch (error) {
        this.$toasted.error(this.errorMessage(error, 'ไม่สามารถคืนสถานะนักศึกษาได้'))
      } finally {
        this.statusSaving = false
      }
    },
    async searchHistory(filters) {
      this.historyLoading = true
      try {
        this.history = await AcademicDataEditService.getHistory(filters)
      } catch (error) {
        this.history = []
        this.$toasted.error(this.errorMessage(error, 'ไม่สามารถค้นหาประวัติการแก้ไขได้'))
      } finally {
        this.historyLoading = false
      }
    },
  },
}
</script>

<style scoped>
.student-id-column {
  flex: 0 0 260px;
  max-width: 260px;
}

.student-search-column {
  flex: 0 0 180px;
  max-width: 180px;
}

@media (max-width: 599px) {
  .student-id-column,
  .student-search-column {
    flex-basis: 100%;
    max-width: 100%;
  }
}
</style>
